import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";
import { Download, ShieldAlert, Users, Activity, Siren, Timer, UserPlus, HeartHandshake, TrendingDown, Repeat } from "lucide-react";
import { Page, BrandHeader, CloseBtn, Spinner } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SOUNDS } from "@/lib/audio";
import { CORE_NEEDS, copingStyleLabel } from "@/lib/schemaFlip";
import { STATE_HEX } from "@/lib/theme";
import { adminCsv } from "@/lib/adminCsv";
import { EngagementAlerts } from "@/components/EngagementAlerts";
import { PilotFeedbackAdmin } from "@/components/PilotFeedback";

const RANGES = [[1, "Today"], [7, "Last 7 days"], [30, "Last month"], [180, "Last 6 months"], [0, "Forever"]];
export const REACH_LABELS = {
  contactCall: "Called trusted person", contactText: "Texted trusted person", crisisCall: "Called crisis line",
  crisisText: "Texted crisis line", beyondBlue: "Called Beyond Blue", blackDog: "Opened Black Dog Institute", okay: "I'm okay for now",
};
export const fmtDuration = (sec) => {
  if (!sec) return "0m";
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return h ? `${h}h ${m}m` : m ? `${m}m ${s}s` : `${s}s`;
};
const relabel = (map, fn) => Object.fromEntries(Object.entries(map || {}).map(([k, v]) => [fn(k), v]));

const Kpi = ({ icon: Icon, label, value, testId }) => (
  <div className="tf-card p-4" data-testid={testId}>
    <Icon size={18} className="text-t-brand" />
    <p className="text-2xl sm:text-3xl font-bold text-t-on mt-2">{value}</p>
    <p className="text-xs text-t-muted">{label}</p>
  </div>
);
const Card = ({ title, children, testId, className = "" }) => (
  <section className={`tf-card p-5 ${className}`} data-testid={testId}><h2 className="font-bold text-t-on mb-3">{title}</h2>{children}</section>
);
function Breakdown({ map, color = "var(--brand)", empty = "No data in this range." }) {
  const rows = Object.entries(map || {}).sort((a, b) => b[1] - a[1]);
  if (!rows.length) return <p className="text-sm text-t-muted">{empty}</p>;
  return (
    <div className="flex flex-col gap-2">
      {rows.slice(0, 10).map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-sm gap-3"><span className="text-t-on2 truncate">{k}</span><b className="text-t-on">{v}</b></div>
          <div className="h-2 rounded-full bg-t-surface3 mt-1"><div className="h-2 rounded-full" style={{ width: `${(v / rows[0][1]) * 100}%`, background: color }} /></div>
        </div>
      ))}
    </div>
  );
}
const Chart = ({ data, dataKey, kind = "line", fmt, ref0 }) =>
  data?.length ? (
    <div className="h-48">
      <ResponsiveContainer>
        {kind === "line" ? (
          <LineChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "var(--muted)" }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fontSize: 11, fill: "var(--muted)" }} domain={ref0 ? [-10, 10] : ["auto", "auto"]} />
            {ref0 ? <ReferenceLine y={0} stroke="var(--border2)" strokeDasharray="4 4" /> : null}
            <Tooltip contentStyle={{ borderRadius: 12 }} formatter={fmt} />
            <Line dataKey={dataKey} stroke="var(--brand)" strokeWidth={3} dot={{ r: 3 }} />
          </LineChart>
        ) : (
          <BarChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "var(--muted)" }} tickFormatter={(d) => d.slice(5)} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--muted)" }} />
            <Tooltip contentStyle={{ borderRadius: 12 }} formatter={fmt} />
            <Bar dataKey={dataKey} fill="var(--brand)" radius={[8, 8, 0, 0]} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  ) : <p className="text-sm text-t-muted">No data in this range.</p>;

function useStats(params) {
  return useQuery({ queryKey: ["admin-stats", params], queryFn: async () => (await api.get("/admin/stats", { params })).data });
}

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [custom, setCustom] = useState(null);
  const [draft, setDraft] = useState({ start: new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10) });
  const tz = new Date().getTimezoneOffset();
  const params = { ...(custom ?? { days }), tz };
  const { data, isLoading, error } = useStats(params);
  const rangeLabel = custom ? `${custom.start} to ${custom.end}` : RANGES.find((r) => r[0] === days)[1];
  const t = data?.totals;
  const needsMap = useMemo(() => relabel(t?.flips?.needs, (k) => CORE_NEEDS.find((n) => n.key === k)?.label ?? k), [t]);

  if (!user?.is_admin) {
    return (
      <Page>
        <div className="tf-card p-10 text-center mt-10" data-testid="admin-denied">
          <ShieldAlert size={36} className="mx-auto text-t-brand" />
          <p className="mt-3 font-bold text-t-on">Admins only</p>
          <Btn className="mt-5 mx-auto" size="md" label="Go home" onClick={() => navigate("/")} />
        </div>
      </Page>
    );
  }

  return (
    <Page className="!max-w-6xl">
      <BrandHeader right={<CloseBtn onClick={() => navigate(-1)} testId="admin-close" />} />
      <div className="mt-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="tf-tag">Admin</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-t-on" data-testid="admin-title">How Triggerific-Pilot is going</h1>
          <p className="mt-2 text-t-on2">Pilot analytics · {rangeLabel}. Counts only — no personal notes or reflections are shown.</p>
        </div>
        <Btn size="md" icon={<Download size={17} />} label="Export CSV" disabled={!data} onClick={() => adminCsv(data, rangeLabel)} testId="admin-export-csv" />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {RANGES.map(([d, l]) => (
          <button key={d} onClick={() => { setCustom(null); setDays(d); }} data-testid={`admin-range-${d}`}
            className={`px-4 h-9 rounded-full text-sm font-semibold border transition-colors ${!custom && days === d ? "bg-t-brand text-t-onbrand border-t-brand" : "bg-t-surface2 text-t-on2 border-t-border"}`}>{l}</button>
        ))}
        <span className="flex items-center gap-2 ml-1">
          <input type="date" className="tf-input !w-auto !py-1.5 !rounded-full text-sm" value={draft.start} onChange={(e) => setDraft({ ...draft, start: e.target.value })} data-testid="admin-custom-start" />
          <span className="text-t-muted text-sm">to</span>
          <input type="date" className="tf-input !w-auto !py-1.5 !rounded-full text-sm" value={draft.end} onChange={(e) => setDraft({ ...draft, end: e.target.value })} data-testid="admin-custom-end" />
          <Btn size="md" variant={custom ? "primary" : "secondary"} className="!h-9" label="Apply" disabled={!draft.start || !draft.end || draft.start > draft.end} onClick={() => setCustom({ ...draft })} testId="admin-custom-apply" />
        </span>
      </div>

      {isLoading ? <div className="mt-10 grid place-items-center"><Spinner className="text-t-brand" /></div> : error ? (
        <p className="mt-8 text-t-on2" data-testid="admin-error">{errText(error)}</p>
      ) : t ? (
        <div className="mt-6 flex flex-col gap-4">
          <EngagementAlerts />
          <PilotFeedbackAdmin />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi icon={Users} label={`Active users (${t.totalUsers} accounts)`} value={t.contributors} testId="kpi-users" />
            <Kpi icon={UserPlus} label="New sign-ups" value={t.newSignups} testId="kpi-signups" />
            <Kpi icon={Activity} label="Check-ins" value={t.totalCheckins} testId="kpi-checkins" />
            <Kpi icon={TrendingDown} label="Avg distress drop" value={t.avgDrop} testId="kpi-drop" />
            <Kpi icon={Siren} label="Get Help Now sessions" value={t.emergencySessions} testId="kpi-emergency" />
            <Kpi icon={Timer} label={`Override time · avg ${fmtDuration(t.avgEmergencySeconds)}`} value={fmtDuration(t.emergencySeconds)} testId="kpi-emergency-time" />
            <Kpi icon={HeartHandshake} label={`Reached out (of ${t.reachOut?.shown ?? 0} reach-out screens)`} value={`${t.reachOutRate}%`} testId="kpi-reach" />
            <Kpi icon={Repeat} label={`Low moods → override (${t.lowMoodOverrides}/${t.lowMoods})`} value={`${t.lowMoodOverrideRate}%`} testId="kpi-lowmood" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card title="New sign-ups by day" testId="admin-signups-chart"><Chart data={t.signupsByDay} dataKey="n" kind="bar" fmt={(v) => [v, "Sign-ups"]} /></Card>
            <Card title="Average mood by day" testId="admin-mood-chart"><Chart data={t.moodByDay} dataKey="avg" ref0 fmt={(v) => [v, "Avg mood"]} /></Card>
            <Card title="Get Help Now time by day" testId="admin-emergency-chart"><Chart data={t.emergencyByDay} dataKey="seconds" kind="bar" fmt={(v) => [fmtDuration(v), "Override time"]} /></Card>
            <Card title="Weekly retention · last 8 weeks" testId="admin-retention">
              <table className="w-full text-sm">
                <thead><tr className="text-t-muted text-left"><th className="py-1 font-medium">Week of</th><th className="font-medium">Active</th><th className="font-medium">Returning</th><th className="font-medium">Return rate</th></tr></thead>
                <tbody>{t.retention.map((w) => <tr key={w.week} className="border-t border-t-border text-t-on"><td className="py-1.5">{w.week}</td><td>{w.active}</td><td>{w.returning}</td><td>{w.rate == null ? "—" : `${w.rate}%`}</td></tr>)}</tbody>
              </table>
            </Card>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Card title="Survival responses (4-F)" testId="admin-fourf">
              {["fight", "flight", "freeze", "fawn"].map((k) => (
                <div key={k} className="flex items-center gap-2 py-1 capitalize"><span className="w-3 h-3 rounded-full" style={{ background: STATE_HEX[k] }} /><span className="flex-1 text-t-on2">{k}</span><b className="text-t-on">{t.fourF?.[k] ?? 0}</b></div>
              ))}
            </Card>
            <Card title="Reach-out actions" testId="admin-reach"><Breakdown map={relabel(Object.fromEntries(Object.entries(t.reachOut || {}).filter(([k]) => k !== "shown")), (k) => REACH_LABELS[k] ?? k)} color="var(--success)" /></Card>
            <Card title="Calming sound picks · current settings" testId="admin-sounds"><Breakdown map={relabel(t.soundPicks, (k) => SOUNDS.find((s) => s.id === k)?.label ?? k)} /></Card>
            <Card title="Coping tools used" testId="admin-coping"><Breakdown map={t.coping} /></Card>
            <Card title="Coping modes" testId="admin-modes"><Breakdown map={t.modes} /></Card>
            <Card title="Schema states" testId="admin-schema-states"><Breakdown map={t.schemaStates} /></Card>
            <Card title="Schemas activated" testId="admin-schemas" className="md:col-span-2"><Breakdown map={t.schemas} color="var(--hot)" /></Card>
            <Card title="Flip the Script" testId="admin-flips">
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[["Flips", t.flips.total], ["Actions done", `${t.flips.done}/${t.flips.actions}`], ["Own scripts", t.flips.ownScripts], ["Flip Coach used", t.flips.coachUsed]].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-t-surface p-3 border border-t-border"><p className="text-xl font-bold text-t-on">{v}</p><p className="text-xs text-t-muted">{l}</p></div>
                ))}
              </div>
              <p className="tf-tag mt-4 mb-2">Coping styles</p>
              <Breakdown map={relabel(t.flips.styles, (k) => copingStyleLabel(k) ?? k)} />
              <p className="tf-tag mt-4 mb-2">Unmet needs</p>
              <Breakdown map={needsMap} />
            </Card>
          </div>

          <Card title={`AI validation feedback (${t.aiFeedback?.total ?? 0} ratings)`} testId="admin-ai-feedback">
            <div className="grid sm:grid-cols-3 gap-3">
              {Object.entries(t.aiFeedback?.byKind ?? {}).map(([k, v]) => (
                <div key={k} className="rounded-xl bg-t-surface p-4 border border-t-border" data-testid={`admin-feedback-${k}`}>
                  <p className="text-xs text-t-muted">{{ "mood-summary": "\"You are heard\" validation", insight: "Closing reflection", "flip-coach": "Flip Coach" }[k]}</p>
                  <p className="text-2xl font-bold text-t-on mt-1">{v.rate == null ? "—" : `${v.rate}% helpful`}</p>
                  <p className="text-xs text-t-muted">{v.helped} helped · {v.not_quite} not quite</p>
                </div>
              ))}
            </div>
            {t.aiFeedback?.comments?.length ? (
              <div className="mt-4 flex flex-col gap-2">
                <p className="tf-tag">Recent comments</p>
                {t.aiFeedback.comments.map((c, i) => (
                  <div key={i} className="rounded-xl border border-t-border p-3 text-sm"><p className="text-t-on">“{c.comment}”</p><p className="text-xs text-t-muted mt-1">{c.kind} · {c.date} · {c.email}</p></div>
                ))}
              </div>
            ) : null}
          </Card>

          <Card title={`Users (${data.rows.length})`} testId="admin-users">
            <div className="overflow-x-auto tf-scroll">
              <table className="w-full text-sm min-w-[880px]">
                <thead><tr className="text-t-muted text-left">{["Email", "Name", "Joined", "Last check-in", "Check-ins", "Triggers", "Schemas", "Fight", "Flight", "Freeze", "Fawn", "Flips", "Healthy wins"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.email} className="border-t border-t-border text-t-on" data-testid={`admin-user-row-${i}`}>
                      <td className="py-2 pr-3">{r.email}</td><td className="pr-3">{r.name}</td><td className="pr-3 whitespace-nowrap">{r.joined.slice(0, 10)}</td><td className="pr-3 whitespace-nowrap">{r.lastActive?.slice(0, 10) ?? "—"}</td>
                      <td className="pr-3">{r.totalCheckins}</td><td className="pr-3">{r.totalTriggers}</td><td className="pr-3">{r.totalSchemaActivations}</td>
                      {["fight", "flight", "freeze", "fawn"].map((k) => <td key={k} className="pr-3">{r.fourF?.[k] ?? 0}</td>)}
                      <td className="pr-3">{r.flips}</td><td>{r.healthyWins}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title={`Churned users (${t.churned.length}) · no check-in for 14+ days`} testId="admin-churned">
            {t.churned.length ? t.churned.map((c) => (
              <div key={c.email} className="flex justify-between py-1.5 border-t first:border-0 border-t-border text-sm"><span className="text-t-on">{c.name} · {c.email}</span><span className="text-t-muted">{c.lastActive} · {c.daysAgo} days ago</span></div>
            )) : <p className="text-sm text-t-muted">Nobody has dropped off. Lovely.</p>}
          </Card>
        </div>
      ) : null}
    </Page>
  );
}
